import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FeUtil, BeUtil, TYPE } from '../../../hezky-routes';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexMarkers, ApexStroke, ApexYAxis, ApexXAxis, ApexTooltip, ApexAnnotations } from "ng-apexcharts";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { DateTime } from 'luxon';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-tr-graph',
  templateUrl: './tr-graph.component.html',
})
export class TrGraphComponent implements OnChanges {
  feUtil: FeUtil = inject(FeUtil);
  beUtil: BeUtil = inject(BeUtil);

  @Input({ required: true, alias: 'metaData' }) metaData!: TYPE.TrMetaDataBase;
  @Input({ required: true, alias: 'viewOptions' }) viewOptions: TYPE.ViewOptions = {};
  @Input({ required: true, alias: 'capabilities' }) capabilities: string[] = [];
  @Input({ required: true, alias: 'alarmData' }) alarmData: any[] = [];

  graphData: any[] = [];
  filterType: 'today' | 'yesterday' | 'week' | 'month'| null = 'today';
  rangeFilters: { value: string, label: string }[] = [];
  loading:boolean = false;
  dateRange:string[] = [];
  date:string[] = [];

  protected series!: ApexAxisChartSeries;
  protected chart!: ApexChart;
  protected dataLabels!: ApexDataLabels;
  protected markers!: ApexMarkers;
  protected fill!: ApexFill;
  protected stroke!: ApexStroke;
  protected yaxis!: ApexYAxis[];
  protected xaxis: ApexXAxis = { type: 'datetime', labels: { datetimeUTC: false } };
  protected tooltip!: ApexTooltip;
  protected annotations!: ApexAnnotations;

  disableDates = (current: Date): boolean => {
    if (this.metaData.trip_start_date) {
      const tripStartDate = DateTime.fromISO(this.metaData.trip_start_date).setZone(this.feUtil.time_zone).startOf('day');
      const endOfToday = DateTime.now().setZone(this.feUtil.time_zone).endOf('day');
      if (current < tripStartDate.toJSDate() || current > endOfToday.toJSDate()) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  };

  ngOnChanges({ metaData }: SimpleChanges): void {
    const newMetaData = metaData?.currentValue;
    const prevMetaData = metaData?.previousValue;
    const isDeepEqual = JSON.stringify(newMetaData) === JSON.stringify(prevMetaData);
    if (isDeepEqual) {
      return;
    }
    if (!newMetaData || !newMetaData.date || !newMetaData.page || !newMetaData.trip_status) {
      return;
    }
    if (newMetaData.trip_start_date) {
      this.determineRangeFilters(newMetaData.trip_start_date);
    }
    if (metaData?.previousValue !== metaData?.currentValue) {
      this.date = [newMetaData.date?.[0], newMetaData.date?.[1]];
      this.initGraph();
      this.initChartData();
    }
    if (newMetaData.date?.[0] && newMetaData.date?.[1] && (newMetaData.trip_status === 'completed' || newMetaData.trip_status === 'discard')) {
      this.dateRange = [newMetaData.date?.[0], newMetaData.date?.[1]];
    }
  }

  private initGraph(): void {
    let id = this.metaData.id;
    if (!id) return;
    if (!this.date?.[0] && !this.date?.[1]) return;
    this.loading = true;
    const params = {
      startDate: this.date?.[0],
      endDate: this.date?.[1],
      timezone: this.feUtil.nLevelPayload?.timezone
    };
    this.beUtil.tripsSvc.getTripReadings(id, params)
      .pipe(untilDestroyed(this), finalize(() => {
        this.loading = false;
        this.updateGraphAnnotation();
      }))
      .subscribe({
        next: ({ data }) => {
          const matrixData = this.beUtil.tripsSvc.getMatrixData(data);
          const combinedData = matrixData.reduce((acc: any, curr) => {
            const { matrix, readingData } = curr;
            const hasValidData = readingData.some((item: any) =>
              item.timeseriesData && item.timeseriesData.some((data: any) => data.value !== null)
            );
            if (!hasValidData) {
              return acc;
            }
            if (!acc[matrix]) {
              acc[matrix] = {
                matrixType: matrix,
                lastReadingData: {},
                chartOptions: {
                  series: []
                }
              };
            }
            const combinedSeries: ApexAxisChartSeries = this.combineSeriesWithSameName(
              readingData.map(({ metaData, timeseriesData }) => ({
                color: this.feUtil.getColor(metaData?.sensorType, metaData?.matrixType),
                name: timeseriesData[0]?.sensorId,
                data: timeseriesData.map(({ datetime, value }: any) => ({
                  x: datetime,
                  y: metaData?.matrixType === 'temperature' && this.feUtil.temp_pref === 'F' ? Number(value * 1.8 + 32).toFixed(1) : value,
                }))
              }))
            );
            acc[matrix].chartOptions.series = combinedSeries;
            acc[matrix].lastReadingData = this.feUtil.groupByKey(
              readingData.flatMap(d => d.timeseriesData),
              'sensorId'
            );
            return acc;
          }, {});
          this.graphData = Object.values(combinedData);
          this.graphData = Array.isArray(this.graphData) && this.graphData.length ? this.graphData.filter((i: any) => this.capabilities.includes(i.matrixType)) : [];
        },
      });
  }

  protected initChartData(): void {
    this.chart = {
      type: 'area',
      stacked: false,
      height: 350,
      zoom: { enabled: this.viewOptions.allowZoom ?? true },
      toolbar: { 
        show: true,
        autoSelected: 'zoom',
        tools: {
          download: this.viewOptions.showDownloadButton ?? true,
          selection: true,
          zoom: this.viewOptions.allowZoom ?? true,
          zoomin: this.viewOptions.allowZoom ?? true,
          zoomout: this.viewOptions.allowZoom ?? true,
          pan: true,
          reset: true
        }
      },
      animations: { enabled: false },
    };

    this.dataLabels = { enabled: false };
    this.markers = { size: 0 };
    this.fill = {
      type: 'gradient',
      gradient: {
        shadeIntensity: 5,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100]
      }
    };
    this.yaxis = [{
      min: (min: number) => min - 0.75,
      max: (max: number) => max + 0.75,
      tickAmount: 5
    }];
    this.xaxis = {
      type: 'datetime',
      labels: { datetimeUTC: false },
      tooltip: {
        enabled: false,
      }
    };
    this.tooltip = {
      enabled: true,
      x: { format: 'MMM d, y HH:mm:ss' }
    };
  }

  private combineSeriesWithSameName(series: any[]): any[] {
    const seriesMap = new Map<string, { name: string, data: any[] }>();
    series.forEach(s => {
      if (seriesMap.has(s.name)) {
        const existingSeries = seriesMap.get(s.name)!;
        s.data.forEach((point: any) => existingSeries.data.push(point));
      } else {
        seriesMap.set(s.name, { 
          ...s,
          data: new Array(s.data.length)
        });
        s.data.forEach((point: any, i: number) => seriesMap.get(s.name)!.data[i] = point);
      }
    });
    return Array.from(seriesMap.values()).map(s => {
      const sortedData = s.data.sort((a, b) => {
        const timeA = a.x instanceof Date ? a.x.getTime() : new Date(a.x).getTime();
        const timeB = b.x instanceof Date ? b.x.getTime() : new Date(b.x).getTime();
        return timeA - timeB;
      });
      return {
        ...s,
        data: sortedData
      };
    });
  }
    
  protected onDateRangeChange(event: Date[]): void {
    if (!event || event.length !== 2) {
      this.filterType = 'today';
      this.onFilterTypeChange('today');
      this.dateRange = [];
      return;
    }
    if (event[0] && event[1]) {
      this.filterType = null;
      const start = DateTime.fromJSDate(event[0]).setZone(this.feUtil.time_zone).startOf('day').toUTC().toISO();
      const end = DateTime.fromJSDate(event[1]).setZone(this.feUtil.time_zone).endOf('day').toUTC().toISO();
      if (start && end) {
        this.dateRange = [start, end];
        this.date[0] = start;
        this.date[1] = end;
      }
      this.initGraph();
    }
  }

  protected onFilterTypeChange(event: string): void {
    const tripStartDate = DateTime.fromISO(this.date[0]);
    switch (event) {
      case 'today': {
        const isTripStartToday = tripStartDate.hasSame(DateTime.now().setZone(this.feUtil.time_zone), 'day');
        if (isTripStartToday) {
          this.date[0] = DateTime.fromISO(this.date[0]).setZone(this.feUtil.time_zone).toUTC().toISO() ?? '';
        } else {
          this.date[0] = DateTime.now().setZone(this.feUtil.time_zone).startOf('day').toUTC().toISO() ?? '';
        }
        this.date[1] = DateTime.now().setZone(this.feUtil.time_zone).endOf('day').toUTC().toISO() ?? '';
        break;
      }
      case 'yesterday': {
        const yesterday = DateTime.now().setZone(this.feUtil.time_zone).minus({ days: 1 });
        const isTripStartYesterday = tripStartDate.hasSame(yesterday, 'day');
        if (isTripStartYesterday) {
          this.date[0] = DateTime.fromISO(this.date[0]).setZone(this.feUtil.time_zone).toUTC().toISO() ?? '';
        } else {
          this.date[0] = yesterday.setZone(this.feUtil.time_zone).startOf('day').toUTC().toISO() ?? '';
        }
        this.date[1] = yesterday.endOf('day').toUTC().toISO() ?? '';
        break;
      }
      case 'month': {
        const now = DateTime.now().setZone(this.feUtil.time_zone);
        const start = now.minus({ days: 30 }).startOf('day');
        this.date[0] = start.toUTC().toISO() ?? '';
        this.date[1] = now.endOf('day').toUTC().toISO() ?? '';
        break;
      }
      default: {
        if (event?.startsWith('last-')) {
          const parts = event.split('-');
          const days = Number(parts[1]);
          let startDate = DateTime.now().setZone(this.feUtil.time_zone).minus({ days: days }).startOf('day');
          this.date[0] = startDate.toUTC().toISO() ?? '';
          this.date[1] = DateTime.now().setZone(this.feUtil.time_zone).endOf('day').toUTC().toISO() ?? '';
          break;
        }
        break;
      }
    }
    this.initGraph();
  }

  private determineRangeFilters(startDate: string) {
    if (!startDate) return;
    const tripStart = DateTime.fromISO(startDate).setZone(this.feUtil.time_zone).startOf('day');
    const today = DateTime.now().setZone(this.feUtil.time_zone).startOf('day');
    const daysDiff = Math.floor(today.diff(tripStart, 'days').days) + 1;
    this.rangeFilters = [];
    this.rangeFilters.push({ value: 'today', label: 'Today' });
    if (daysDiff >= 2) {
      this.rangeFilters.push({ value: 'yesterday', label: 'Yesterday' });
    }
    if (daysDiff > 2 && daysDiff <= 7) {
      this.rangeFilters.push({ value: `last-${daysDiff}`, label: `Last ${daysDiff} Days` });
    } else if (daysDiff > 7) {
      this.rangeFilters.push({ value: `last-7`, label: 'Last 7 Days' });
      this.rangeFilters.push({ value: 'month', label: 'Last 30 Days' });
    }
  }

  private updateGraphAnnotation(): void {
    if (!Array.isArray(this.graphData) || this.graphData.length === 0) return;
    const matrixType = this.graphData[0]?.matrixType?.toLowerCase();
    if (!matrixType) return;
    const flatAlarms = this.alarmData.flat();
    const matchingAlarms = flatAlarms.filter(
      (alarm: any) => alarm?.threshold_on?.toLowerCase() === matrixType
    );
    const yaxisData: any[] = [];
    for (const alarm of matchingAlarms) {
      const { threshold_value, severity, threshold_condition } = alarm;
      const color =
        severity === 'High' ? '#e60707' :
        severity === 'Medium' ? '#FF8D0F' : '#eaaa08';
      yaxisData.push({
        y: matrixType === 'temperature' && this.feUtil.temp_pref === 'F'
          ? Number(threshold_value * 1.8 + 32).toFixed(1)
          : threshold_value,
        borderColor: color,
        borderWidth: 3,
        label: {
          text: `Severity: ${severity} | Condition: ${threshold_condition}`,
          style: {
            color: "#fff",
            fontSize: "9px",
            background: color,
          }
        }
      });
    }
    if (yaxisData.length) {
      for (const item of this.graphData) {
        if (item?.chartOptions) item.chartOptions.annotations = { yaxis: yaxisData };
      }
    }
  }
}
